import { useEffect, useRef, useState } from 'react';

import type { UseMutateAsyncFunction } from '@tanstack/react-query';

import type {
  CreateBatchRoomRatesParams,
  CreateBatchRoomRatesResponse,
} from '~/hooks/apis/reservation/useCreateBatchRoomRates';

import { getDistinctAppliedRoomRates } from '../CapacityStep/utils';
import type { FlashRatePlanDraft } from '../RatePlanStep/utils';
import type { FlashOfferRoomDraft } from '../RoomsStep/utils';
import type { AppliedFlashRoomRate } from '../types';
import { alwaysActionable } from '../types';

import { applyRoomRates } from './applyRoomRates';
import { buildRoomRateRooms, pruneSelectionToKnownRatePlans, pruneSelectionToKnownRooms } from './utils';

export interface UseRoomRatesStepParams {
  createBatchRoomRates: UseMutateAsyncFunction<CreateBatchRoomRatesResponse, unknown, CreateBatchRoomRatesParams>;
  createdRatePlans: Array<App.RatePlan>;
  isRoomRatesStep: boolean;
  propertyId: string | undefined;
  ratePlanDrafts: Array<FlashRatePlanDraft>;
  roomDrafts: Array<FlashOfferRoomDraft>;
  roomTypes: Array<App.RoomType>;
}

/**
 * Owns the Room Rates step (5): picking which draft Flash rate plan(s) apply to each room
 * (existing or drafted), and later (at Submission, once rooms/rate plans are real) batch-
 * creating the resulting room rates for real via `handleApplyRoomRates`.
 */
export function useRoomRatesStep({
  createBatchRoomRates,
  createdRatePlans,
  isRoomRatesStep,
  propertyId,
  ratePlanDrafts,
  roomDrafts,
  roomTypes,
}: UseRoomRatesStepParams) {
  const [appliedFlashRoomRates, setAppliedFlashRoomRates] = useState<Array<AppliedFlashRoomRate>>([]);
  const [storedRoomRatePlanIds, setStoredRoomRatePlanIds] = useState<Record<string, Array<string>>>({});
  const [hasAppliedRoomRates, setHasAppliedRoomRates] = useState(false);
  const [isCreatingRoomRates, setIsCreatingRoomRates] = useState(false);
  const defaultRoomRateSelectionKey = useRef<string | null>(null);

  const ratePlanDraftIds = ratePlanDrafts.map((draft) => draft.draftId);
  const roomRateRooms = buildRoomRateRooms({ ratePlanDrafts, roomDrafts, roomTypes });
  // Derived so a removed draft (or another property's room) can't linger as a selection key or value.
  const selectedRoomRatePlanIds = pruneSelectionToKnownRatePlans(
    pruneSelectionToKnownRooms(
      storedRoomRatePlanIds,
      roomRateRooms.map((room) => room.id),
    ),
    ratePlanDraftIds,
  );
  const selectableRoomRateRoomTypeIds = roomRateRooms.filter((room) => !room.hasRatePlan).map((room) => room.id);
  // Derived, not latched -- unlike a one-shot state flag, this un-derives itself once a newly
  // added room makes something selectable again.
  const hasNothingToSelect =
    ratePlanDrafts.length > 0 && roomRateRooms.length > 0 && selectableRoomRateRoomTypeIds.length === 0;
  const isRoomRatesStepReadOnly = hasAppliedRoomRates || hasNothingToSelect;
  const isRoomRatesStepNextDisabled =
    isRoomRatesStep &&
    !isRoomRatesStepReadOnly &&
    (ratePlanDrafts.length === 0 ||
      isCreatingRoomRates ||
      Object.values(selectedRoomRatePlanIds).every((ratePlanIds) => ratePlanIds.length === 0));

  // A draft-room-rate-plan pairing shaped like a real applied room rate, so Package Setup's
  // preview matching (name-based) can run against drafts exactly as it will against real data
  // once Submission has created everything for real.
  const draftRoomRateAssignments: Array<AppliedFlashRoomRate> = Object.entries(selectedRoomRatePlanIds).flatMap(
    ([roomKey, selectedRatePlanDraftIds]) => {
      const room = roomRateRooms.find((candidate) => candidate.id === roomKey);

      return selectedRatePlanDraftIds.map((ratePlanDraftId) => {
        const ratePlanDraft = ratePlanDrafts.find((draft) => draft.draftId === ratePlanDraftId);

        return {
          ratePlanId: ratePlanDraftId,
          ratePlanName: ratePlanDraft?.ratePlanName,
          roomName: room?.name ?? roomKey,
          roomRateId: `${roomKey}:${ratePlanDraftId}`,
          roomTypeId: roomKey,
        };
      });
    },
  );
  const appliedRatePlanNamesByRoomTypeId = appliedFlashRoomRates.reduce<Record<string, Array<string>>>(
    (acc, roomRate) => {
      const label = roomRate.ratePlanName || roomRate.ratePlanId;
      const existingLabels = acc[roomRate.roomTypeId] ?? [];

      if (!existingLabels.includes(label)) {
        acc[roomRate.roomTypeId] = existingLabels.concat(label);
      }

      return acc;
    },
    {},
  );
  const distinctAppliedRoomRates = getDistinctAppliedRoomRates(appliedFlashRoomRates);

  // Defaults every selectable room to every rate plan draft the first time this rate-plan set
  // is seen, so the CM starts from "all selected" rather than an empty selection.
  useEffect(() => {
    if (
      !isRoomRatesStep ||
      hasAppliedRoomRates ||
      ratePlanDraftIds.length === 0 ||
      selectableRoomRateRoomTypeIds.length === 0
    ) {
      return;
    }

    // Includes which rooms are selectable, not just which rate plans exist -- a room added later
    // (same rate plans) must still get its own default selection, not be silently skipped.
    const selectionKey = `${ratePlanDraftIds.join(',')}|${selectableRoomRateRoomTypeIds.join(',')}`;

    if (defaultRoomRateSelectionKey.current === selectionKey) {
      return;
    }

    defaultRoomRateSelectionKey.current = selectionKey;
    // Only rooms without a selection of a current rate plan get a default, so this can't undo a
    // manual deselect on a room already in the set.
    setStoredRoomRatePlanIds((prev) => {
      const newlySelectableRoomTypeIds = selectableRoomRateRoomTypeIds.filter(
        (roomTypeId) => !prev[roomTypeId]?.some((ratePlanId) => ratePlanDraftIds.includes(ratePlanId)),
      );

      return newlySelectableRoomTypeIds.length === 0
        ? prev
        : {
            ...prev,
            ...Object.fromEntries(newlySelectableRoomTypeIds.map((roomTypeId) => [roomTypeId, ratePlanDraftIds])),
          };
    });
  }, [hasAppliedRoomRates, isRoomRatesStep, ratePlanDraftIds, selectableRoomRateRoomTypeIds]);

  function handleSelectRoomRatePlans(roomTypeId: string, ratePlanIds: Array<string>) {
    if (isRoomRatesStepReadOnly) {
      return;
    }

    setStoredRoomRatePlanIds((prev) => {
      if (ratePlanIds.length === 0) {
        const next = { ...prev };
        delete next[roomTypeId];
        return next;
      }

      return { ...prev, [roomTypeId]: ratePlanIds };
    });
  }

  function selectAllRooms() {
    if (isRoomRatesStepReadOnly) {
      return;
    }

    setStoredRoomRatePlanIds(
      ratePlanDraftIds.length > 0
        ? Object.fromEntries(selectableRoomRateRoomTypeIds.map((roomTypeId) => [roomTypeId, ratePlanDraftIds]))
        : {},
    );
  }

  function clearAllRooms() {
    if (isRoomRatesStepReadOnly) {
      return;
    }

    setStoredRoomRatePlanIds({});
  }

  // Real creation, called only once everything it depends on (rooms/rate plans) is real.
  // Submission passes its own freshly-resolved `overrides` (real ids remapped from this step's
  // draft-keyed selection, and the just-created rate plans) rather than relying on this hook's
  // own closed-over state, which can't have observed those updates yet within the same call.
  async function handleApplyRoomRates(overrides?: {
    createdRatePlans?: Array<App.RatePlan>;
    selectedRoomRatePlanIds?: Record<string, Array<string>>;
  }): Promise<{
    createdRoomRates: Array<AppliedFlashRoomRate>;
    errorMessage?: string;
    reusedRoomRates: Array<AppliedFlashRoomRate>;
    success: boolean;
  }> {
    setIsCreatingRoomRates(true);

    const roomRateRoomsByRoomTypeId = new Map(roomRateRooms.map((room) => [room.id, room.name]));

    const { createdRoomRates, errorMessage, reusedRoomRates, success } = await applyRoomRates({
      createBatchRoomRates,
      createdRatePlans: overrides?.createdRatePlans ?? createdRatePlans,
      propertyId,
      roomRateRoomsByRoomTypeId,
      selectedRoomRatePlanIds: overrides?.selectedRoomRatePlanIds ?? selectedRoomRatePlanIds,
    });

    setIsCreatingRoomRates(false);

    // Only marks itself "applied" (locking the step read-only) on this call's own success, never
    // eagerly -- a failure leaves the selection untouched so a retry re-attempts the full set.
    if (!success) {
      return { createdRoomRates, errorMessage, reusedRoomRates, success: false };
    }

    setAppliedFlashRoomRates((prev) => prev.concat(createdRoomRates, reusedRoomRates));
    setHasAppliedRoomRates(true);
    return { createdRoomRates, reusedRoomRates, success: true };
  }

  // Clears appliedFlashRoomRates/hasAppliedRoomRates (this step's read-only markers); leaves the
  // CM's own selection untouched so a retry doesn't force reselecting everything.
  function resetAfterFailedSubmission() {
    setAppliedFlashRoomRates([]);
    setHasAppliedRoomRates(false);
  }

  function reset() {
    setAppliedFlashRoomRates([]);
    setStoredRoomRatePlanIds({});
    setHasAppliedRoomRates(false);
    setIsCreatingRoomRates(false);
    defaultRoomRateSelectionKey.current = null;
  }

  return {
    appliedFlashRoomRates,
    appliedRatePlanNamesByRoomTypeId,
    clearAllRooms,
    resetAfterFailedSubmission,
    distinctAppliedRoomRates,
    draftRoomRateAssignments,
    handleApplyRoomRates,
    handleSelectRoomRatePlans,
    hasAppliedRoomRates,
    isCreatingRoomRates,
    isRoomRatesStepNextDisabled,
    isRoomRatesStepReadOnly,
    isSubmissionFailureActionable: alwaysActionable,
    reset,
    roomRateRooms,
    selectAllRooms,
    selectedRoomRatePlanIds,
    setIsCreatingRoomRates,
  };
}
